import { AHQueryHandlerLoading, AHText, useTheme } from "arkh";
import { StyleSheet, View } from "react-native";

import { PollForTokenScreen } from "./PollForTokenScreen";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { StorecastScreen } from "../features/Storecast/StorecastScreen";
import { authService } from "./auth/authService";
import { useActor } from "@xstate/react";
import {
  graphql,
  RelayEnvironmentProvider,
  useLazyLoadQuery,
} from "react-relay";
import { createRelayEnvironment } from "../Client";

import { IntlProvider } from "react-intl";

import { MainLocaleQuery } from "./__generated__/MainLocaleQuery.graphql";
import { Settings } from "luxon";
import { getLocaleMessages } from "./getLocaleMessages";
import { ScreenScaled } from "../common/ScreenScaled";
import { SubscriptionStatus } from "../subscriptionStatus";

const styles = StyleSheet.create({
  container: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFF",
    flexDirection: "column",
  },
});

export function Main(props: { guessLocale: "sv" | "en" }) {
  const [currentState] = useActor(authService);

  const theme = useTheme();

  if (
    (currentState.matches("pollingForToken") ||
      currentState.matches("waitingForPollingToken")) &&
    currentState.context.userCode
  ) {
    return (
      <ScreenScaled>
        <PollForTokenScreen userCode={currentState.context.userCode} />
      </ScreenScaled>
    );
  }

  if (currentState.matches("requestingCode")) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <AHText>Loading...</AHText>
      </View>
    );
  }
  if (currentState.matches("requestingCodeError")) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <AHText
          type="title-small"
          style={{ textAlign: "center", marginLeft: 100, marginRight: 100 }}
        >
          Error encountered when trying to connect to the authorization server.
          Please check the internet connection.
        </AHText>
        <AHText type="body">Retrying...</AHText>
      </View>
    );
  }
  if (currentState.matches("loggedIn")) {
    return <LoggedIn guessLocale={props.guessLocale} />;
  }

  return (
    <View style={{ flex: 1 }}>
      <AHText type="body">Retrying...</AHText>
    </View>
  );
}

function LoggedIn(props: { guessLocale: "sv" | "en" }) {
  const [systemLanguage, setSystemLanguage] = useState<"sv" | "en">(
    props.guessLocale
  );

  const [subscriptionStatus, _setSubscriptionStatus] =
    useState<SubscriptionStatus>(SubscriptionStatus.init);

  const setSubscriptionStatus = useCallback(
    (status: SubscriptionStatus) => {
      _setSubscriptionStatus((prev) => {
        if (
          status === SubscriptionStatus.connecting &&
          prev !== SubscriptionStatus.init
        ) {
          return SubscriptionStatus.reconnecting;
        }
        return status;
      });
    },
    [_setSubscriptionStatus]
  );

  useEffect(() => {
    Settings.defaultLocale = systemLanguage;
  }, [systemLanguage]);
  const relayEnv = useRef(
    createRelayEnvironment(systemLanguage, setSubscriptionStatus)
  );

  const setNewLocale = useCallback(
    (newLocale: string) => {
      if (systemLanguage !== newLocale) {
        relayEnv.current.dispose();
        relayEnv.current = createRelayEnvironment(
          newLocale,
          setSubscriptionStatus
        );
        setSystemLanguage(newLocale.includes("sv") ? "sv" : "en");
      }
    },
    [systemLanguage, setSubscriptionStatus]
  );

  return (
    <IntlProvider
      defaultLocale="en"
      locale={systemLanguage}
      messages={getLocaleMessages(systemLanguage)}
    >
      <RelayEnvironmentProvider environment={relayEnv.current.env}>
        <Suspense fallback={<AHQueryHandlerLoading />}>
          <StorecastScreen subscriptionStatus={subscriptionStatus} />
        </Suspense>
        <Suspense fallback={null}>
          <Locale setLocale={setNewLocale} />
        </Suspense>
      </RelayEnvironmentProvider>
    </IntlProvider>
  );
}

function Locale({ setLocale }: { setLocale: (l: string) => void }) {
  const query = useLazyLoadQuery<MainLocaleQuery>(
    graphql`
      query MainLocaleQuery {
        currentStore {
          locale
          timezone
        }
      }
    `,
    {}
  );
  useEffect(() => {
    if (query.currentStore?.timezone) {
      Settings.defaultZone = query.currentStore.timezone;
    }
    if (query.currentStore?.locale) {
      setLocale(query.currentStore.locale);
    }
  }, [query.currentStore?.locale, query.currentStore?.timezone, setLocale]);

  return null;
}
